/**
 * @file MeetingIntelSummaryReport.tsx
 * @brief Company report — Meeting Intel summary + meeting rows.
 *
 * @references
 * - GET /api/company/reports/meeting-intel
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import {
  Activity,
  Clock3,
  Scale,
  SquareCheckBig,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { GridCell } from "@/features/analytics/components/GridBlock";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import {
  formatDateTime,
  formatDurationSeconds,
} from "@/shared/utils/formatters";
import { useMeetingIntelReport } from "../api";
import { defaultReportRange } from "../defaultRange";
import { percentChange } from "@/features/dashboard/kpiCompare";
import {
  previousRange,
  previousRangeLabel,
} from "@/features/reports-shared/previousRange";
import { ReportShell } from "./ReportShell";

function scoreLabel(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toFixed(1);
}

export function MeetingIntelSummaryReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const queryRange = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );
  const report = useMeetingIntelReport(queryRange);
  const summary = report.data?.summary;
  const meetings = report.data?.meetings ?? [];
  const byStatus = report.data?.by_status ?? [];
  // The same-length window before, for the +/-% pills (03-10-2026).
  const prevQueryRange = useMemo(() => previousRange(queryRange), [queryRange]);
  const prevSummary = useMeetingIntelReport(prevQueryRange).data?.summary;
  const compareLabel = previousRangeLabel(queryRange);
  const delta = (now: unknown, before: unknown) =>
    prevSummary ? percentChange(Number(now ?? 0), Number(before ?? 0)) : undefined;

  return (
    <ReportShell
      title="Meeting Intel summary"
      description="Company boardroom meetings in the selected range (up to 500 rows exported)."
      range={range}
      onRangeChange={setRange}
      exportDisabled={meetings.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "meeting-intel",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-5">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Meetings"
              icon={BoardroomTable} // DES 08-10-2026: app icon, was Presentation
              value={String(summary?.meeting_count ?? 0)}
              hint={compareLabel}
              delta={delta(summary?.meeting_count, prevSummary?.meeting_count)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Duration"
              icon={Clock3}
              value={formatDurationSeconds(summary?.total_duration_seconds)}
              hint={compareLabel}
              delta={delta(summary?.total_duration_seconds, prevSummary?.total_duration_seconds)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Avg engagement"
              icon={Activity}
              value={scoreLabel(summary?.avg_engagement)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Avg sentiment"
              icon={Scale}
              value={scoreLabel(summary?.avg_sentiment)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Action items"
              icon={SquareCheckBig}
              value={String(summary?.total_action_items ?? 0)}
              delta={delta(summary?.total_action_items, prevSummary?.total_action_items)}
              hint={`${summary?.total_decisions ?? 0} decisions`}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        {byStatus.length > 0 ? (
          <>
            <ReportSectionHeader title="By status" />
            <GridCell>
              <div className="flex flex-wrap gap-2 text-sm">
                {byStatus.map((row) => (
                  <span
                    key={String(row.status)}
                    className="rounded-full bg-secondary px-3 py-1 capitalize"
                  >
                    {row.status || "unknown"} · {row.count}
                  </span>
                ))}
              </div>
            </GridCell>
          </>
        ) : null}

        <ReportSectionHeader
          title="Meetings"
          description="Newest first. Open Meeting Intel for full detail."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Meeting",
              "Status",
              { label: "Duration", align: "right" },
              { label: "Engagement", align: "right" },
              { label: "Actions", align: "right" },
              { label: "Owner", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={meetings.length === 0}
          >
            {meetings.map((row) => (
              <tr key={row.id} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <div className="font-medium">{row.title || "Untitled"}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(row.started_at)}
                  </div>
                </td>
                <td className="px-4 py-2 capitalize text-muted-foreground">
                  {row.status || "—"}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatDurationSeconds(row.total_duration_seconds)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {scoreLabel(row.engagement_score)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.action_item_count}
                </td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {[row.owner_first_name, row.owner_last_name]
                    .filter(Boolean)
                    .join(" ") ||
                    row.owner_email ||
                    "—"}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
